import { filterDOMProps } from '@react-aria/utils';
import clsx from 'clsx';
import React from 'react';

import { Icon } from '@gitbook/spine-icons';

import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { HeadlessTextInput } from '../TextInput';
import { Toolbar } from '../Toolbar';
import { Tooltip } from '../Tooltip';
import { renderIcon } from '../util';

export const CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT = 10;

export interface CodeBlockProps {
    children: React.ReactNode;
    className?: string;
}

export interface CodeBlockConfiguration {
    collapsedLineCount: number;
    editable: boolean;
    expandable: boolean;
    numberOfLines?: number;
    overflow: 'scroll' | 'wrap';
    showLineNumbers: boolean;
}

export interface CodeBlockState {
    isExpanded: boolean;
    bodyId?: string;
}

export type CodeBlockContextType = CodeBlockConfiguration & CodeBlockState;

const CodeBlockContext = React.createContext<CodeBlockContextType>({
    collapsedLineCount: CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT,
    editable: false,
    overflow: 'scroll',
    showLineNumbers: false,
    numberOfLines: undefined,
    expandable: false,
    isExpanded: false,
});

/**
 * Component to render a code block.
 */
export function CodeBlock(
    props: CodeBlockProps & Partial<CodeBlockContextType>
): React.ReactElement {
    const {
        children,
        className,
        collapsedLineCount = CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT,
        editable = false,
        expandable = false,
        isExpanded = false,
        numberOfLines,
        overflow = 'scroll',
        showLineNumbers = false,
    } = props;

    const bodyId = React.useId();
    const value = React.useMemo(
        () => ({
            bodyId,
            collapsedLineCount,
            editable,
            expandable: Boolean(expandable),
            isExpanded,
            numberOfLines: numberOfLines,
            overflow,
            showLineNumbers,
        }),
        [
            collapsedLineCount,
            showLineNumbers,
            editable,
            overflow,
            numberOfLines,
            expandable,
            bodyId,
            isExpanded,
        ]
    );

    return (
        <CodeBlockContext.Provider value={value}>
            <div
                className={clsx(
                    'group/code-block relative w-full select-none rounded border border-base bg-muted py-2',
                    className
                )}
            >
                {children}
            </div>
        </CodeBlockContext.Provider>
    );
}
const EXPAND_ANIMATION_THRESHOLD = 20;

export function CodeBlockBody(props: { children: React.ReactNode }) {
    const { children } = props;
    const { collapsedLineCount, expandable, isExpanded, bodyId, numberOfLines } =
        React.useContext(CodeBlockContext);
    const differenceInLineCount = numberOfLines ? numberOfLines - collapsedLineCount : 0;
    const isAnimated = expandable && differenceInLineCount <= EXPAND_ANIMATION_THRESHOLD;
    const collapsedHeight = `${collapsedLineCount * 1.5}rem`; // assuming line height of 1.5rem
    return (
        <React.Suspense fallback={null}>
            <div
                className={clsx(
                    'w-full overflow-auto',
                    expandable && [
                        'overflow-y-hidden [transition-behavior:allow-discrete] after:pointer-events-none after:absolute after:inset-0 after:bg-gradient-to-t after:from-0% after:from-[--sp-color-bg-muted] after:to-70% after:to-transparent after:content-[""]',
                        isAnimated &&
                            'motion-safe:transition-[height] motion-safe:delay-100 motion-safe:duration-100',
                        isExpanded
                            ? '[height:calc-size(auto,size)] after:opacity-0'
                            : 'after:opacity-100',
                    ]
                )}
                style={expandable && !isExpanded ? { height: collapsedHeight } : {}}
                id={bodyId}
            >
                <CodeBlockContent>{children}</CodeBlockContent>
            </div>
        </React.Suspense>
    );
}

function CodeBlockContent(props: { children: React.ReactNode }) {
    const { children } = props;

    const { overflow } = React.useContext(CodeBlockContext);
    return (
        <div
            translate="no"
            spellCheck={false}
            className={clsx(
                'grid w-full grid-cols-[auto_minmax(0,_1fr)] [count-reset:line] print:whitespace-pre-wrap',
                overflow === 'scroll' ? 'min-w-max' : 'min-w-full'
            )}
        >
            {children}
        </div>
    );
}

/**
 * Optional header for the code block.
 */
export function CodeBlockHeader(props: { children: React.ReactNode }) {
    return (
        <div
            contentEditable={false}
            className="size-full w-3/4 select-none text-wrap pr-4 pl-5 text-muted text-ui-base"
            {...props}
        />
    );
}

/**
 * Resize the input element based on its content. If the browser supports it, use the `field-sizing: content` CSS property instead.
 */
export function CodeBlockEditableTitle(props: {
    title?: string;
    onChange?: (title: string) => void;
    onEscape?: () => void;
}) {
    const { title, onChange, onEscape } = props;
    const supportsFieldSizing = CSS.supports('field-sizing: content');

    const [inputValue, setInputValue] = React.useState(title);
    return (
        <div
            data-value={inputValue}
            className={clsx(
                'relative',
                // if browser does not support field-sizing:content, the simplest way to resize the input properly is to use grid and a pseudo element with the input value as its content.
                // tailwind not-supports doesn't seem to work with pseudo-elements.
                !supportsFieldSizing &&
                    'grid after:invisible after:block after:whitespace-pre-wrap after:content-[attr(data-value)_"_"] *:[grid-area:1/1/2/2] after:[grid-area:1/1/2/2]'
            )}
        >
            <HeadlessTextInput
                inputClassName="min-w-full resize-none text-muted text-ui-base supports-[field-sizing:content]:[field-sizing:content]" // field-sizing:content support is still limited as of 10/2025
                placeholder="Enter a filename"
                multiline
                rows={1}
                onChange={(event) => setInputValue(event.target.value)}
                onBlur={() => onChange?.(inputValue ?? '')}
                value={inputValue ?? ''}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        onEscape?.();
                    }
                }}
            />
        </div>
    );
}

/**
 * Optional footer for the code block. Not part of editable content.
 */
export function CodeBlockFooter(props: { children: React.ReactNode; className?: string }) {
    return <div contentEditable={false} {...props} />;
}

/**
 * Footer with expand/collapse button for the code block.
 */
export function CodeBlockExpandableFooter(props: { onChange: (isExpanded: boolean) => void }) {
    const { numberOfLines, isExpanded, bodyId } = React.useContext(CodeBlockContext);
    return (
        <CodeBlockFooter className="pointer-events-none absolute bottom-2 flex w-full justify-center">
            <Button
                label={isExpanded ? 'Collapse' : `See all ${numberOfLines} lines`}
                leadingIcon={renderIcon(Icon.ChevronDown, {
                    className: clsx(
                        'origin-center transition-transform will-change-transform',
                        isExpanded ? 'rotate-180' : ''
                    ),
                })}
                size="small"
                kind="secondary"
                onPress={() => {
                    props.onChange(!isExpanded);
                }}
                className="pointer-events-auto opacity-0 focus:opacity-100 group-hover/code-block:opacity-100"
                aria-expanded={isExpanded}
                aria-controls={bodyId}
            />
        </CodeBlockFooter>
    );
}

/**
 * Toolbar container for the code block. Non-editable.
 */
export function CodeBlockToolbar(props: { children: React.ReactNode; show?: boolean }) {
    const { children, show = false } = props;
    return (
        <div className="@container pointer-events-none absolute inset-0" contentEditable={false}>
            <Toolbar
                size="small"
                className={clsx(
                    'pointer-events-auto absolute top-2 right-2 flex items-center gap-1 transition-all group-hover/code-block:opacity-100 supports-[container-type:inline-size]:@[12rem]:flex supports-[container-type:inline-size]:hidden',
                    show ? 'opacity-100' : 'opacity-0'
                )}
            >
                {children}
            </Toolbar>
        </div>
    );
}

interface CodeToken {
    content: string;
    color: string;
}

interface CodeBlockLineProps {
    tokens?: CodeToken[];
    children?: React.ReactNode;
    number: number;
    onSelect?: () => void;
    highlighting?: 'highlighted' | 'added' | 'deleted' | 'modified' | null;
}

/**
 * Renderer  for a single line in a code block.
 */
export const CodeBlockLine = React.forwardRef<HTMLDivElement, CodeBlockLineProps>(
    function CodeBlockLine(props, ref) {
        const { number, tokens, children, highlighting, onSelect, ...domProps } = props;
        const { overflow, showLineNumbers, editable } = React.useContext(CodeBlockContext);
        return (
            <div
                {...(domProps ? filterDOMProps(domProps) : {})}
                ref={ref}
                className={clsx(
                    'group/code-block-line col-span-2 grid min-w-full select-none grid-cols-subgrid font-medium font-mono text-ui-base leading-6',
                    { wrap: 'whitespace-pre-wrap', scroll: 'whitespace-pre pr-4' }[overflow],
                    highlighting
                        ? {
                              highlighted: 'bg-info-standout',
                              added: 'bg-diff-added',
                              deleted: 'bg-diff-deleted',
                              modified: 'bg-diff-modified',
                          }[highlighting]
                        : editable
                          ? 'hover:bg-info-standout'
                          : null
                )}
            >
                <CodeBlockLineGutter onSelect={onSelect} highlighting={highlighting}>
                    {showLineNumbers ? <CodeBlockLineNumber number={number} /> : '\u200B'}
                </CodeBlockLineGutter>
                <div className="w-full min-w-0 select-text">
                    {tokens ? <CodeBlockLineTokens tokens={tokens} /> : children}
                </div>
            </div>
        );
    }
);

/**
 * Renders the syntax highlighted tokens for a code block line.
 */
function CodeBlockLineTokens(props: { tokens: CodeToken[] }) {
    const { tokens } = props;
    return (
        <>
            {tokens.map((token, tokenIndex) => (
                <span key={tokenIndex} style={{ color: token.color }}>
                    {token.content}
                </span>
            ))}
        </>
    );
}

/**
 * Left gutter for a code block line, optionally with a line highlight checkbox. Not content-editable.
 */
function CodeBlockLineGutter(props: {
    children: React.ReactNode;
    onSelect?: () => void;
    highlighting?: 'highlighted' | 'added' | 'deleted' | 'modified' | null;
}) {
    const { children, onSelect, highlighting } = props;

    const { editable } = React.useContext(CodeBlockContext);
    return (
        // biome-ignore lint/a11y: checkbox handles focus and keyboard interaction this just increases the hit target
        <div
            onClick={onSelect}
            className={clsx(
                'group/code-block-line-gutter relative flex w-full min-w-0 select-none justify-end pr-3 pl-2',
                {
                    'cursor-pointer': editable,
                    'cursor-default': !editable,
                }
            )}
            contentEditable={false}
        >
            {children}
            {editable ? (
                <div className="invisible absolute inset-0 flex bg-info-standout pt-[0.5lh] pl-0.75 opacity-0 transition-discrete transition-opacity group-hover/code-block-line-gutter:visible group-hover/code-block-line-gutter:opacity-100">
                    <Tooltip label="Highlight line" position="bottom">
                        <Checkbox
                            size="xsmall"
                            value={highlighting === 'highlighted'}
                            onChange={onSelect}
                            className="-translate-y-1/2"
                        />
                    </Tooltip>
                </div>
            ) : null}
        </div>
    );
}

/**
 * Renders the line number for a code block line.
 */
function CodeBlockLineNumber({ number }: { number: number }) {
    return <span className="mr-2 select-none font-mono text-muted tabular-nums">{number}</span>;
}

interface VirtualizedCodeBlockLineProps {
    children: React.ReactNode;
    attributes?: React.HTMLAttributes<HTMLDivElement>;
    style?: React.CSSProperties;
}

/**
 * A simplified code block line, rendered with virtualization.
 */
export const VirtualizedCodeBlockLine = React.forwardRef<
    HTMLDivElement,
    VirtualizedCodeBlockLineProps
>(function VirtualizedCodeBlockLine(props, ref) {
    const { showLineNumbers, numberOfLines } = React.useContext(CodeBlockContext);
    return (
        <div
            className={clsx(
                'col-span-2 grid min-w-full select-none grid-cols-subgrid whitespace-pre font-medium font-mono text-ui-small leading-6'
            )}
            {...props.attributes}
            style={props.style}
            data-virtual
            ref={ref}
        >
            <span aria-hidden className="mr-2 pr-3 pl-2 tabular-nums">
                {showLineNumbers ? `${numberOfLines}` : '\u200B'}
            </span>
            {props.children}
        </div>
    );
});
